import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  Row,
  TabContent,
  Table,
  TabPane,
  NavLink,
  Badge,
  Tooltip
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { toast } from "react-toastify";
import DeleteModal from "../../Components/Common/DeleteModal";
import { deleteCoupon, listCouponUsages } from "./_requests";
import { useProfile } from "../../Components/Hooks/UserHooks";
import { formatDate } from "../../helpers/FunctionHelpers";

const ViewCouponDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const couponDetails = location.state?.couponDetailsView;
  const { userProfile } = useProfile();
  const [activeTab, setActiveTab] = useState("1");
  const [couponUsages, setCouponUsages] = useState<any[]>([]);
  const [loadingUsages, setLoadingUsages] = useState(false);

  const toggleTab = (tab: string) => setActiveTab(tab);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (activeTab === "2" && id) {
      fetchCouponUsages();
    }
  }, [activeTab, id]);

  const fetchCouponUsages = async () => {
    setLoadingUsages(true);
    try {
      const data = {
        coupon_id: id,
      };
      const response = await listCouponUsages(data);
      setCouponUsages(response.data);
    } catch (error) {
      toast.error("Failed to load coupon usages");
    } finally {
      setLoadingUsages(false);
    }
  };

  // If no details, redirect back
  if (!couponDetails) {
    navigate("/coupon");
    return null;
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCoupon(id, userProfile?.userId);
      toast.success("Coupon deleted successfully");
      setDeleteModal(false);
      navigate("/coupon");
    } catch (err) {
      toast.error("Failed to delete coupon");
      setDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const getAmountDisplay = () => {
    return couponDetails.amount_type === 1
      ? `${couponDetails.coupon_value}%`
      : `$${couponDetails.coupon_value}`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="View Coupon" pageTitle="Coupons" />

        <Row>
          <Col lg={12}>
            <Card className="shadow-lg border-0 rounded-3 p-4 position-relative">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="text-primary fw-bold">{couponDetails?.code}</h3>

                <div>
                  <Tooltip
                    placement="top"
                    target="DeleteTooltip"
                    onClick={() => setDeleteModal(true)}
                  >
                    Delete
                  </Tooltip>
                  <span
                    onClick={() => setDeleteModal(true)}
                    id="DeleteTooltip"
                    className="btn btn-light"
                  >
                    <i className="mdi mdi-trash-can-outline align-bottom"></i>
                  </span>
                </div>
              </div>

              <CardBody>
                {/* Tabs */}
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggleTab("1")}
                    >
                      Coupon Details
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => toggleTab("2")}
                    >
                      Usage History
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="mt-4">
                  {/* Coupon Details Tab */}
                  <TabPane tabId="1">
                    <Row>
                      <Col lg={6}>
                        <Card className="border-1 shadow-sm rounded p-3 bg-light">
                          <h5 className="text-secondary mb-4">Coupon Information</h5>
                          <Table borderless className="mb-0">
                            <tbody>
                              <tr>
                                <th style={{ width: "40%" }}>Coupon Code:</th>
                                <td>
                                  <Badge color="success" className="fs-6">
                                    {couponDetails?.code || "N/A"}
                                  </Badge>
                                </td>
                              </tr>
                              <tr>
                                <th>Discount Value:</th>
                                <td>{getAmountDisplay()}</td>
                              </tr>
                              <tr>
                                <th>Minimum Order:</th>
                                <td>${couponDetails?.minimum_order_value || "0"}</td>
                              </tr>
                              <tr>
                                <th>Usage Limit:</th>
                                <td>{couponDetails?.usage_count || "Unlimited"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card>
                      </Col>

                      <Col lg={6}>
                        <Card className="border-1 shadow-sm rounded p-3 bg-light">
                          <h5 className="text-secondary mb-4">Validity</h5>
                          <Table borderless className="mb-0">
                            <tbody>
                              <tr>
                                <th style={{ width: "40%" }}>Start Date:</th>
                                <td>{formatDate(couponDetails?.start_date)}</td>
                              </tr>
                              <tr>
                                <th>End Date:</th>
                                <td>{formatDate(couponDetails?.end_date)}</td>
                              </tr>
                              <tr>
                                <th>Status:</th>
                                <td>
                                  <Badge
                                    color={couponDetails?.is_active ? "success" : "danger"}
                                    className="fs-6"
                                  >
                                    {couponDetails?.is_active ? "Active" : "Inactive"}
                                  </Badge>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </Card>
                      </Col>

                      <Col lg={12} className="mt-4">
                        <Card className="border-1 shadow-sm rounded p-3 bg-light">
                          <h5 className="text-secondary mb-3">Description</h5>
                          <p className="mb-0">
                            {couponDetails?.description || "No description available"}
                          </p>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>

                  {/* Usage History Tab */}
                  <TabPane tabId="2">
                    <Card className="border-1 shadow-sm rounded p-3 bg-light">
                      <h5 className="text-secondary mb-4">Coupon Usage History</h5>
                      {loadingUsages ? (
                        <div className="text-center py-4">Loading usages...</div>
                      ) : couponUsages.length > 0 ? (
                        <div className="table-responsive">
                          <Table striped bordered hover>
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Order Code</th>
                                <th>User</th>
                                <th>Total Amount</th>
                                <th>Discount Applied</th>
                                <th>Used At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {couponUsages.map((usage, index) => (
                                <tr key={usage.id}>
                                  <td>{index + 1}</td>
                                  <td>{usage.order?.order_code_id || 'N/A'}</td>
                                  <td>{usage.user?.name || 'N/A'}</td>
                                  <td>${usage.order?.total_amount || '0.00'}</td>
                                  <td>
                                    {usage.coupon?.amount_type == 1
                                      ? `${usage.coupon?.coupon_value}%`
                                      : `$${usage.coupon?.coupon_value}`}
                                  </td>
                                  <td>{formatDate(usage.used_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-4">No usage history found for this coupon</div>
                      )}
                    </Card>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        content={
          <>
            Do you want to delete coupon <strong>{couponDetails?.code}</strong>?
          </>
        }
      />
    </div>
  );
};

export default ViewCouponDetails;